<template>
    <!-- Mobile drawer toggle (hamburger) — only visible <= 900px via CSS.
         Rendered for every page since each page mounts its own <AppSidebar>. -->
    <button v-if="loggedIn" ref="mobileToggle" type="button" class="sidebar-mobile-toggle" @click="openDrawer"
        aria-label="Open menu" :aria-expanded="mobileOpen" aria-controls="app-sidebar">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
    </button>

    <!-- Dim overlay behind the open drawer; tap to close. -->
    <div v-if="loggedIn && mobileOpen" class="sidebar-overlay" @click="closeDrawer(true)"></div>

    <!-- Collapsed, the rail expands as an overlay while the pointer is over it
         (peek) and folds back when it leaves. --sidebar-width stays at the
         rail width, so the page underneath never shifts. -->
    <aside v-if="loggedIn" id="app-sidebar" ref="aside" class="glass-sidebar"
        :class="{ collapsed: railView, peek: isPeeking, 'mobile-open': mobileOpen }" @mouseenter="onPointerEnter"
        @mouseleave="onPointerLeave" @keydown.esc="closeDrawer(true)">

        <!-- Head: brand + sidebar toggle. The toggle lives up here, next to the
             brand, instead of beside Logout at the bottom where a slip of the
             pointer signed people out. -->
        <div class="sidebar-head">
            <button type="button" class="sidebar-brand" @click="goToDashboard" aria-label="BidSnap AI home"
                @mouseenter="showTip($event, 'BidSnap AI')" @mouseleave="hideTip" @focus="showTip($event, 'BidSnap AI')"
                @blur="hideTip">
                <span class="logo-glow" aria-hidden="true">
                    <svg viewBox="0 0 64 64" width="34" height="34" fill="none">
                        <defs>
                            <linearGradient id="sidebarg" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0" stop-color="#34f0c0" />
                                <stop offset="1" stop-color="#0ea5e9" />
                            </linearGradient>
                        </defs>
                        <!-- BidSnap mark: snap-frame corners capturing a checkmark -->
                        <g stroke="url(#sidebarg)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M23 15h-4a4 4 0 0 0-4 4v4" />
                            <path d="M41 15h4a4 4 0 0 1 4 4v4" />
                            <path d="M23 49h-4a4 4 0 0 1-4-4v-4" />
                            <path d="M41 49h4a4 4 0 0 0 4-4v-4" />
                        </g>
                        <path d="M23 33.5l6.5 6.5L44 25" stroke="url(#sidebarg)" stroke-width="7" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </span>
                <span v-if="!railView" class="brand-text-dark" aria-hidden="true">BidSnap<span
                        class="brand-text-accent"> AI</span></span>
            </button>

            <button type="button" class="collapse-btn" @click="toggleCollapsed"
                :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                :aria-expanded="!sidebarCollapsed" aria-controls="app-sidebar" aria-keyshortcuts="["
                @mouseenter="showTip($event, collapseTip, true)" @mouseleave="hideTip"
                @focus="showTip($event, collapseTip, true)" @blur="hideTip">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <line x1="9" y1="4" x2="9" y2="20" />
                </svg>
            </button>
        </div>

        <nav class="sidebar-nav" aria-label="Main">
            <!-- Dynamic Items (If provided) -->
            <template v-if="items && items.length">
                <button v-for="item in items" :key="item.id" type="button" class="sidebar-pill"
                    :class="{ active: activeItem === item.id }" :aria-current="activeItem === item.id ? 'page' : null"
                    @mouseenter="showTip($event, item.label)" @mouseleave="hideTip" @focus="showTip($event, item.label)"
                    @blur="hideTip" @click="handleItemClick(item)">
                    <span class="icon-wrapper" aria-hidden="true" v-html="item.icon"></span>
                    <span class="pill-label" :class="{ 'sr-only': railView }">{{ item.label }}</span>
                </button>

                <!-- Optional Back Button -->
                <button v-if="backButton" type="button" class="sidebar-pill back-btn"
                    @mouseenter="showTip($event, backButton.label)" @mouseleave="hideTip"
                    @focus="showTip($event, backButton.label)" @blur="hideTip" @click="handleBackClick">
                    <span class="icon-wrapper" aria-hidden="true" v-html="backButton.icon"></span>
                    <span class="pill-label" :class="{ 'sr-only': railView }">{{ backButton.label }}</span>
                </button>
            </template>

            <!-- Default Static Items (If no dynamic items provided) -->
            <template v-else>
                <!-- Pre-Bid / Post-Bid groups. Collapsed, the group headers give way
                     to a divider and every item stays visible as an icon, so no page
                     becomes unreachable when the sidebar is narrow. -->
                <div v-for="group in groups" :key="group.key" class="sidebar-group">
                    <button v-if="!railView && groupsCollapsible" type="button" class="group-header"
                        :class="{ open: openGroups[group.key] }"
                        :aria-expanded="openGroups[group.key]" :aria-controls="`sidebar-group-${group.key}`"
                        @click="toggleGroup(group.key)">
                        <span class="group-icon" aria-hidden="true" v-html="group.icon"></span>
                        <span class="group-title">{{ group.label }}</span>
                        <!-- A closed group still flags urgent work inside it -->
                        <span v-if="!openGroups[group.key] && groupBadge(group)" class="nav-dot nav-dot--inline"
                            aria-hidden="true"></span>
                        <svg class="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                            aria-hidden="true">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <!-- A single group has nothing to fold into, so its name is a plain
                         heading: no chevron, items always shown. Same row height as
                         the button, so the items sit exactly where they always do. -->
                    <div v-else-if="!railView" :id="`sidebar-group-label-${group.key}`"
                        class="group-header group-header--static">
                        <span class="group-title">{{ group.label }}</span>
                    </div>
                    <!-- Same height as the header it replaces, so nothing below moves -->
                    <div v-else class="group-divider group-divider--header" role="presentation"></div>

                    <div :id="`sidebar-group-${group.key}`" class="group-items"
                        :role="groupsCollapsible ? null : 'group'"
                        :aria-labelledby="!groupsCollapsible && !railView ? `sidebar-group-label-${group.key}` : null"
                        v-show="railView || !groupsCollapsible || openGroups[group.key]">
                        <button v-for="item in group.items" :key="item.id" type="button" class="sidebar-pill sub"
                            :class="{ active: activeItem === item.id, 'plan-locked': isPlanLocked(item) }"
                            :aria-current="activeItem === item.id ? 'page' : null"
                            @mouseenter="showTip($event, tipFor(item))" @mouseleave="hideTip"
                            @focus="showTip($event, tipFor(item))" @blur="hideTip"
                            @click="item.go()">
                            <span class="icon-wrapper" aria-hidden="true">
                                <span v-html="item.icon"></span>
                                <span v-if="railView && item.badge" class="nav-dot nav-dot--icon"></span>
                            </span>
                            <span class="pill-label" :class="{ 'sr-only': railView }">{{ item.label }}</span>
                            <span v-if="item.badge && !railView" class="nav-badge" aria-hidden="true">
                                {{ item.badge.count }}
                            </span>
                            <span v-if="item.badge" class="sr-only">, {{ item.badge.label }}</span>
                            <span v-if="isPlanLocked(item)" class="sr-only">(not in your plan)</span>
                            <svg v-if="isPlanLocked(item) && !railView" class="module-lock-icon" width="14"
                                height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                aria-hidden="true">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </button>

                    </div>
                </div>

                <!-- ===================== STANDARD DOCUMENTS ===================== -->
                <!-- Searchable by every logged-in user; only Super Admins can upload
                     (enforced inside the page and by the API). Re-enable by adding
                     it to topItems with go: goToStandards. -->

                <!-- Company Knowledge Base, Admin Panel (admin only) -->
                <div v-if="topItems.length" class="sidebar-top-items">
                    <div class="group-divider" role="presentation"></div>
                    <button v-for="item in topItems" :key="item.id" type="button" class="sidebar-pill"
                        :class="{ active: activeItem === item.id, 'plan-locked': isPlanLocked(item) }"
                        :aria-current="activeItem === item.id ? 'page' : null"
                        @mouseenter="showTip($event, tipFor(item))" @mouseleave="hideTip"
                        @focus="showTip($event, tipFor(item))" @blur="hideTip" @click="item.go()">
                        <span class="icon-wrapper" aria-hidden="true" v-html="item.icon"></span>
                        <span class="pill-label" :class="{ 'sr-only': railView }">{{ item.label }}</span>
                        <span v-if="isPlanLocked(item)" class="sr-only">(not in your plan)</span>
                        <svg v-if="isPlanLocked(item) && !railView" class="module-lock-icon" width="14" height="14"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    </button>
                </div>
            </template>
        </nav>

        <!-- Bottom bar: logout, on its own -->
        <div class="sidebar-bottom-bar">
            <button type="button" class="sidebar-pill logout-btn" @click="handleLogout"
                @mouseenter="showTip($event, 'Logout')" @mouseleave="hideTip" @focus="showTip($event, 'Logout')"
                @blur="hideTip">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    aria-hidden="true">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span class="pill-label" :class="{ 'sr-only': railView }">Logout</span>
            </button>
        </div>

        <!-- Prompt for items outside the current plan -->
        <LoginPromptModal :show="showLoginPrompt" :message="lockPromptMessage" :requiresLogin="lockPromptRequiresLogin"
            @close="showLoginPrompt = false" />
    </aside>

    <!-- Rail tooltip. Native title tooltips wait ~1s and never appear on
         keyboard focus; this one shows immediately for both. Screen readers
         already get the label from the button, so it is aria-hidden. -->
    <Teleport to="body">
        <div v-if="loggedIn && tip.visible" class="rail-tooltip" aria-hidden="true"
            :style="{ top: tip.top + 'px', left: tip.left + 'px' }">
            {{ tip.text }}
        </div>
    </Teleport>
</template>

<script>
import LoginPromptModal from './LoginPromptModal.vue';
import { isLoggedIn, isAdmin as userIsAdmin } from '../utils/auth';
import { useBookmarks } from '../store/bookmarks';
import { isClosingSoon } from '../utils/dateUtils';
import { POST_BID_ENABLED } from '../config/features';

// Storage keys. Every page mounts its own sidebar, so anything not stored here
// resets on each navigation.
// sidebar_collapsed: '1' collapsed, '0' expanded, absent = follow window width.
const COLLAPSED_KEY = 'sidebar_collapsed';
const GROUPS_KEY = 'sidebar_groups';

// Hover peek: a short delay to open so passing the pointer across the rail
// doesn't flash it open, and a longer one to close so a slight overshoot
// doesn't snap it shut.
const PEEK_OPEN_MS = 150;
const PEEK_CLOSE_MS = 300;

// Below this width the sidebar folds to an icon rail on its own, unless the
// person has chosen otherwise. At 900px and below it becomes a drawer.
// Only on pointer devices: the rail leans on hover (peek, tooltips), so a
// touch tablet keeps the labelled sidebar.
const AUTO_RAIL_QUERY = '(max-width: 1199px) and (hover: hover)';
const DRAWER_QUERY = '(max-width: 900px)';

function readStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function writeStorage(key, value) {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch (e) {
        // Storage unavailable (private mode) — the choice just won't persist.
    }
}

function matches(query) {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches;
}

const ICONS = {
    preBid: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>',
    postBid: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    search: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    bookmark: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',
    file: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
    upload: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>',
    activity: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>',
    archive: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>',
    book: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>',
    shield: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><circle cx="12" cy="12" r="3"/></svg>',
};

const GROUP_OF = {
    tenders: 'pre', bookmarks: 'pre', files: 'pre', upload: 'pre',
    active: 'post', completed: 'post',
};

// Open by default: Pre-Bid. Post-Bid opens when the current page is inside it
// (see data()).
const DEFAULT_OPEN_GROUPS = { pre: true, post: false };

// Reads the remembered open/closed state for the known group keys.
function readOpenGroups() {
    let saved = {};
    try {
        saved = JSON.parse(readStorage(GROUPS_KEY) || '{}') || {};
    } catch (e) {
        saved = {};
    }
    const open = { ...DEFAULT_OPEN_GROUPS };
    Object.keys(DEFAULT_OPEN_GROUPS).forEach((key) => {
        if (typeof saved[key] === 'boolean') open[key] = saved[key];
    });
    return open;
}

export default {
    name: 'AppSidebar',
    components: {
        LoginPromptModal
    },
    props: {
        activeItem: {
            type: String,
            default: 'upload'
        },
        items: {
            type: Array,
            default: null
        },
        backButton: {
            type: Object,
            default: null
        },

    },
    setup() {
        const { bookmarks, ensureLoaded } = useBookmarks();
        return { bookmarks, ensureLoadedBookmarks: ensureLoaded };
    },
    data() {
        // Group open/closed state is remembered; the group holding the current
        // page always opens so the active item is never hidden.
        const openGroups = readOpenGroups();
        const activeGroup = GROUP_OF[this.activeItem];
        if (activeGroup) openGroups[activeGroup] = true;

        const pref = readStorage(COLLAPSED_KEY);
        return {
            openGroups,
            // null = automatic (follow window width); otherwise the person's choice.
            collapsePref: pref === '1' ? 'collapsed' : pref === '0' ? 'expanded' : null,
            autoRail: matches(AUTO_RAIL_QUERY),
            isDrawer: matches(DRAWER_QUERY),
            showLoginPrompt: false,
            // Off-canvas drawer state (mobile only; ignored on desktop where the
            // sidebar is always visible).
            mobileOpen: false,
            peeking: false,
            tip: { visible: false, text: '', top: 0, left: 0 },
            lockPromptMessage: "This section is only available after login. Please login to continue.",
            lockPromptRequiresLogin: true
        };
    },
    computed: {
        loggedIn() {
            return isLoggedIn();
        },
        // The drawer always opens full width with labels; above that, the
        // person's choice wins, and without one the window width decides.
        sidebarCollapsed() {
            if (this.isDrawer) return false;
            if (this.collapsePref) return this.collapsePref === 'collapsed';
            return this.autoRail;
        },
        // Rail open as an overlay under the pointer (only while collapsed).
        isPeeking() {
            return this.peeking && this.sidebarCollapsed && !this.isDrawer;
        },
        // What the sidebar looks like right now: an icon rail, unless peeking.
        railView() {
            return this.sidebarCollapsed && !this.isPeeking;
        },
        collapseTip() {
            return `${this.sidebarCollapsed ? 'Expand' : 'Collapse'} sidebar  [`;
        },
        // Same rule as the router guard: admin or superadmin.
        isAdmin() {
            return userIsAdmin();
        },
        // A company with no plan assigned has no module restrictions (matches
        // the backend's plan_service default — enforcement only kicks in once
        // a plan is actually assigned). enabled_modules comes from /profile.
        enabledModules() {
            try {
                const userData = JSON.parse(localStorage.getItem("user-info"));
                return userData?.enabled_modules || null;
            } catch (e) {
                return null;
            }
        },
        // Bookmarked tenders closing within 72 hours — the one count here that
        // asks for action. Kept to deadlines on purpose: a badge on everything
        // stops meaning anything.
        bookmarksClosingSoon() {
            return (this.bookmarks || []).filter((b) => isClosingSoon(b.closing_date)).length;
        },
        groups() {
            const n = this.bookmarksClosingSoon;
            const pre = [
                // Tender Search — always public, never plan-gated
                { id: 'tenders', label: 'Tender Search', icon: ICONS.search, module: null, go: this.goToTenders },
                {
                    id: 'bookmarks', label: 'Bookmarks', icon: ICONS.bookmark, module: 'tender_search', go: this.goToBookmarks,
                    badge: n ? { count: n, label: `${n} closing within 3 days` } : null,
                },
                { id: 'files', label: 'Bids', icon: ICONS.file, module: 'bid_management', go: this.goToFiles },
            ];
            // Tender Synopsis (upload) — admin only, and plan-gated
            if (this.isAdmin) {
                pre.push({ id: 'upload', label: 'Tender Synopsis', icon: ICONS.upload, module: 'bid_management', go: this.goToUpload });
            }
            const groups = [{ key: 'pre', label: 'Pre-Bid', icon: ICONS.preBid, items: pre }];
            // Post-Bid is switched off for now (config/features.js).
            if (!POST_BID_ENABLED) return groups;
            return [
                ...groups,
                {
                    key: 'post', label: 'Post-Bid', icon: ICONS.postBid, items: [
                        { id: 'active', label: 'Active Projects', icon: ICONS.activity, module: 'bid_management', go: this.goToActive },
                        { id: 'completed', label: 'Completed Projects', icon: ICONS.archive, module: 'bid_management', go: this.goToCompleted },
                    ]
                },
            ];
        },
        // Folding only helps when there's more than one group to choose
        // between; with Post-Bid off, Pre-Bid stands alone as a heading.
        groupsCollapsible() {
            return this.groups.length > 1;
        },
        topItems() {
            if (!this.isAdmin) return [];
            return [
                { id: 'knowledge', label: 'Company Knowledge Base', icon: ICONS.book, module: 'bid_management', go: this.goToKnowledge },
                { id: 'admin', label: 'Admin Panel', icon: ICONS.shield, module: null, go: this.goToAdmin },
            ];
        }
    },
    watch: {
        sidebarCollapsed: {
            handler(val) {
                document.documentElement.style.setProperty('--sidebar-width', val ? '60px' : '220px');
                if (!val) {
                    this.hideTip();
                    this.peeking = false;
                }
            },
            immediate: true
        },
        isPeeking(val) {
            if (val) this.hideTip();
        },
        openGroups: {
            handler(val) {
                writeStorage(GROUPS_KEY, JSON.stringify(val));
            },
            deep: true
        },
        // Close the mobile drawer whenever the route changes (i.e. after the user
        // taps a menu item and navigation completes).
        '$route'() {
            this.closeDrawer(false);
            this.hideTip();
            this.clearPeekTimer();
            this.peeking = false;
        }
    },
    mounted() {
        if (window.matchMedia) {
            this.railMql = window.matchMedia(AUTO_RAIL_QUERY);
            this.drawerMql = window.matchMedia(DRAWER_QUERY);
            this.onRailChange = (e) => { this.autoRail = e.matches; };
            this.onDrawerChange = (e) => {
                this.isDrawer = e.matches;
                if (!e.matches) this.mobileOpen = false;
            };
            this.railMql.addEventListener('change', this.onRailChange);
            this.drawerMql.addEventListener('change', this.onDrawerChange);
        }
        document.addEventListener('keydown', this.onShortcut);
        if (this.loggedIn && this.isModuleEnabled('tender_search')) {
            this.ensureLoadedBookmarks();
        }
    },
    beforeUnmount() {
        if (this.railMql) this.railMql.removeEventListener('change', this.onRailChange);
        if (this.drawerMql) this.drawerMql.removeEventListener('change', this.onDrawerChange);
        document.removeEventListener('keydown', this.onShortcut);
        this.clearPeekTimer();
    },
    methods: {
        onPointerEnter() {
            if (!this.sidebarCollapsed || this.isDrawer) return;
            this.clearPeekTimer();
            this.peekTimer = setTimeout(() => { this.peeking = true; }, PEEK_OPEN_MS);
        },
        onPointerLeave() {
            this.clearPeekTimer();
            if (!this.peeking) return;
            this.peekTimer = setTimeout(() => { this.peeking = false; }, PEEK_CLOSE_MS);
        },
        clearPeekTimer() {
            if (this.peekTimer) {
                clearTimeout(this.peekTimer);
                this.peekTimer = null;
            }
        },
        toggleCollapsed() {
            this.clearPeekTimer();
            this.peeking = false;
            const next = this.sidebarCollapsed ? 'expanded' : 'collapsed';
            // Choosing the state the window width would pick anyway hands control
            // back to the automatic behaviour; anything else is remembered.
            const auto = this.autoRail ? 'collapsed' : 'expanded';
            this.collapsePref = next === auto ? null : next;
            writeStorage(COLLAPSED_KEY, this.collapsePref === null ? null : (this.collapsePref === 'collapsed' ? '1' : '0'));
        },
        // "[" toggles the sidebar, like a pane toggle in desktop apps. Ignored
        // while typing and with modifier keys so it never steals input.
        onShortcut(e) {
            if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
            if (this.isDrawer || !this.loggedIn) return;
            const t = e.target;
            if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
            e.preventDefault();
            this.toggleCollapsed();
        },
        openDrawer() {
            this.mobileOpen = true;
            // Move focus into the drawer so keyboard and screen-reader users
            // land on the menu they just opened.
            this.$nextTick(() => {
                const aside = this.$refs.aside;
                const target = aside && (aside.querySelector('[aria-current="page"]') || aside.querySelector('nav button'));
                if (target) target.focus();
            });
        },
        // restoreFocus: return focus to the hamburger when the drawer is
        // dismissed (Esc, overlay) — not after a navigation.
        closeDrawer(restoreFocus) {
            if (!this.mobileOpen) return;
            this.mobileOpen = false;
            if (restoreFocus && this.$refs.mobileToggle) {
                this.$nextTick(() => this.$refs.mobileToggle && this.$refs.mobileToggle.focus());
            }
        },
        // Rail tooltips appear only while collapsed (labels are hidden then);
        // `always` is for icon-only controls that never show a label.
        // Also shown in the open sidebar when a label is cut off by the
        // ellipsis, so the full name is always one hover away.
        showTip(event, text, always = false) {
            if (!text || this.isDrawer) return;
            const el = event.currentTarget;
            const label = el.querySelector && el.querySelector('.pill-label');
            const truncated = !this.railView && !!label && label.scrollWidth > label.clientWidth;
            if (!this.railView && !always && !truncated) return;
            const rect = el.getBoundingClientRect();
            this.tip = { visible: true, text, top: rect.top + rect.height / 2, left: rect.right + 10 };
        },
        hideTip() {
            if (this.tip.visible) this.tip = { ...this.tip, visible: false };
        },
        tipFor(item) {
            if (this.isPlanLocked(item)) return `${item.label}: not in your plan`;
            if (item.badge) return `${item.label} · ${item.badge.label}`;
            return item.label;
        },
        groupBadge(group) {
            return group.items.some((item) => item.badge);
        },
        // Gate for everything except Tenders: shows the login popup and stops
        // navigation. The router guard is the real protection — this is UX.
        requireLogin() {
            if (!this.loggedIn) {
                this.lockPromptMessage = "This section is only available after login. Please login to continue.";
                this.lockPromptRequiresLogin = true;
                this.showLoginPrompt = true;
                return false;
            }
            return true;
        },
        // `enabledModules === null` means no plan-based restriction (no plan
        // assigned, or a Super Admin company) — matches the backend default-allow.
        isModuleEnabled(module) {
            if (!this.enabledModules) return true;
            return this.enabledModules.includes(module);
        },
        isPlanLocked(item) {
            return !!item.module && !this.isModuleEnabled(item.module);
        },
        // UX-only gate mirroring requireLogin — the real enforcement is the
        // backend 403 (ModuleNotInPlanException) on the underlying API calls.
        requireModule(module) {
            if (!this.isModuleEnabled(module)) {
                this.lockPromptMessage = "This feature isn't included in your current plan. Contact your administrator to upgrade.";
                this.lockPromptRequiresLogin = false;
                this.showLoginPrompt = true;
                return false;
            }
            return true;
        },
        toggleGroup(group) {
            if (!this.requireLogin()) return;
            this.openGroups[group] = !this.openGroups[group];
        },
        goToTenders() {
            // Always public — never plan-gated.
            if (this.activeItem !== 'tenders') {
                this.$router.push({ name: 'Tenders' });
            }
        },
        goToBookmarks() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('tender_search')) return;
            if (this.activeItem !== 'bookmarks') {
                this.$router.push({ name: 'Bookmarks' });
            }
        },
        goToDashboard() {
            if (this.activeItem !== 'tenders') {
                this.$router.push({ name: 'Dashboard' });
            }
        },
        goToUpload() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('bid_management')) return;
            if (this.activeItem !== 'upload') {
                this.$router.push({ name: 'Home' });
            }
        },
        goToFiles() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('bid_management')) return;
            if (this.activeItem !== 'files') {
                this.$router.push({ name: 'BidListing' });
            }
        },
        goToAdmin() {
            if (!this.requireLogin()) return;
            // Always push with an empty query. A project-scoped view (e.g.
            // Contract Docs opened from one project) lives on this same route
            // with ?tab=&project_id=, so clearing the query is what tells the
            // admin page to reset to a clean Overview.
            if (this.activeItem !== 'admin' || Object.keys(this.$route.query || {}).length) {
                this.$router.push({ name: 'CompanyAdmin', query: {} });
            }
        },
        goToKnowledge() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('bid_management')) return;
            if (this.activeItem !== 'knowledge') {
                this.$router.push({ name: 'CompanyKnowledgeBase' });
            }
        },
        goToStandards() {
            if (!this.requireLogin()) return;
            if (this.activeItem !== 'standards') {
                this.$router.push({ name: 'StandardDocuments' });
            }
        },
        goToActive() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('bid_management')) return;
            if (this.activeItem !== 'active') {
                this.$router.push({ name: 'ActiveProjects' });
            }
        },
        goToCompleted() {
            if (!this.requireLogin()) return;
            if (!this.requireModule('bid_management')) return;
            if (this.activeItem !== 'completed') {
                this.$router.push({ name: 'CompletedProjects' });
            }
        },
        handleItemClick(item) {
            this.$emit('item-click', item);
            if (item.action) {
                item.action();
            }
        },
        handleBackClick() {
            if (this.backButton && this.backButton.action) {
                this.backButton.action();
            }
        },
        handleLogout() {
            localStorage.removeItem('user-info');
            localStorage.removeItem('user-name');
            window.location.href = '/';
        }
    }
}
</script>

<style scoped>
/* =====================================================================
   Geometry. The rail and the open sidebar share one grid: every row has a
   fixed height and every icon sits on the same vertical axis (x = 30px,
   the centre of the 60px rail). Opening only widens the panel and reveals
   labels — nothing moves up, down or sideways. Before, the stacked head,
   the header/divider swap, different paddings and labels wrapping during
   the width animation shifted rows by up to 44px between states.
   ===================================================================== */
.glass-sidebar {
    /* Brief and decelerating: the panel starts at full speed under the
       pointer and settles, with the peek shadow fading alongside. */
    transition: width 0.22s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.22s ease;
    --row-h: 40px;
    /* nav rows */
    --header-h: 32px;
    /* group headers and the dividers that replace them */
    --head-h: 40px;
    /* brand row */
    --icon-inset: 8px;
    /* (36px rail content - 20px icon) / 2 */
}

/* Ensure all sidebar content sits above the ::before grid overlay */
.glass-sidebar>* {
    position: relative;
    z-index: 1;
}

/* The global focus ring (primary-600) is too dark to see on the navy panel. */
.glass-sidebar :focus-visible {
    outline: 2px solid #7dd3fc;
    outline-offset: 2px;
}

/* ===== Head: brand + sidebar toggle — one row, one height, both states ===== */
.sidebar-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    height: var(--head-h);
    margin: 1rem 0 1.25rem 0;
    flex-shrink: 0;
}

.sidebar-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    height: 100%;
    /* logo 34px: 12px panel padding + 1px = centred on the 30px axis */
    padding: 0 0 0 1px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;
}

.logo-glow {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    filter: drop-shadow(0 2px 6px rgba(26, 161, 126, 0.4));
}

.brand-text-dark {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.brand-text-accent {
    background: linear-gradient(135deg, #34f0c0 0%, #0ea5e9 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

/* Sidebar toggle */
.collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.75);
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.collapse-btn:hover {
    background: rgba(0, 113, 197, 0.3);
    color: #fff;
    border-color: rgba(0, 113, 197, 0.5);
}

/* In the rail the toggle sits on top of the logo instead of stacking under
   it (which made the head 44px taller). Pointer users never need it there —
   hovering the rail opens the sidebar, toggle included — so it stays
   invisible until keyboard focus reaches it. */
.glass-sidebar.collapsed .collapse-btn {
    position: absolute;
    left: 2px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    background: #001F3F;
}

.glass-sidebar.collapsed .collapse-btn:focus-visible {
    opacity: 1;
}

/* Touch screens have no hover to open the rail, so there the toggle simply
   replaces the logo. */
@media (hover: none) {
    .glass-sidebar.collapsed .collapse-btn {
        opacity: 1;
    }

    .glass-sidebar.collapsed .logo-glow {
        visibility: hidden;
    }
}

/* The drawer (<= 900px) is dismissed with the overlay or Esc; collapsing an
   overlay makes no sense. Repeated here because this scoped rule outranks
   the global one in responsive.css. */
@media (max-width: 900px) {
    .collapse-btn {
        display: none;
    }
}

/* ===== Nav list — scrolls on short windows instead of clipping items ===== */
.sidebar-nav {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
}

.sidebar-nav::-webkit-scrollbar {
    display: none;
}

.sidebar-top-items {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

/* ===== Rows: identical in the rail and the open sidebar =====
   Same height and left inset in both states, so the icon never moves and
   the rail needs no centring rule. One size for every row (the old 13.6px
   sub-items added a second type size for no reason). */
.glass-sidebar .sidebar-pill {
    height: var(--row-h);
    padding: 0 0.75rem 0 var(--icon-inset);
    gap: 0.75rem;
    font-size: var(--fs-nav);
    line-height: 20px;
    overflow: hidden;
}

/* Labels never wrap. While the panel animates from 60px to 220px a wrapping
   label reflowed through two and three lines and bounced the rows; now it
   is clipped, and a long one ends in an ellipsis with the full name in a
   tooltip. */
.pill-label {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
}

.icon-wrapper>span {
    display: flex;
}

.back-btn {
    opacity: 0.8;
}

.back-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
}

/* ===== Bottom bar: logout ===== */
.sidebar-bottom-bar {
    margin-top: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 0.75rem;
    flex-shrink: 0;
}

/* ===== Groups (Find / Bids / Projects) ===== */
.sidebar-group {
    display: flex;
    flex-direction: column;
}

.group-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    height: var(--header-h);
    /* 18px icon on the same 30px axis as the 20px row icons */
    padding: 0 0.75rem 0 9px;
    border: 0;
    background: transparent;
    text-align: left;
    color: rgba(255, 255, 255, 0.65);
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.2s, color 0.2s;
    user-select: none;
    font-family: var(--font-sans);
    overflow: hidden;
}

.group-header:hover {
    color: #fff;
    background: rgba(0, 113, 197, 0.1);
}

/* Lone group: a section label, not a control. It reads as a heading the way
   macOS sidebar sections do: no icon, smaller and quieter than the items,
   sitting just above them. Height stays --header-h so the items below don't
   move between the rail and the open sidebar. Text starts on the items'
   icon edge. white@0.6 on #001F3F = 6.6:1. */
.group-header--static,
.group-header--static:hover {
    align-items: flex-end;
    padding: 0 0.75rem 6px 10px;
    cursor: default;
    color: rgba(255, 255, 255, 0.6);
    background: transparent;
}

.group-header--static .group-title {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.group-icon {
    display: flex;
    flex-shrink: 0;
    opacity: 0.85;
}

/* Title-style group names ("Find"). Hierarchy comes from size, weight and
   the muted color rather than indentation, so item icons can share the
   rail's axis. */
.group-title {
    flex: 1;
    min-width: 0;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
}

/* Sets the admin-only items apart from the bid workflow. */
.group-divider {
    height: 1px;
    margin: 0.35rem 0.5rem;
    background: rgba(255, 255, 255, 0.14);
    flex-shrink: 0;
}

/* Stands in for a group header in the rail: the header's full height with
   the line drawn through its middle. */
.group-divider--header {
    height: var(--header-h);
    margin: 0 0.5rem;
    background: linear-gradient(rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.14)) center / 100% 1px no-repeat;
}

.chevron {
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: rotate(-90deg);
    opacity: 0.7;
}

.group-header.open .chevron {
    transform: rotate(0deg);
}

.group-items {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-top: 0.3rem;
}

/* ===== Deadline badge (Bookmarks closing within 72h) =====
   Amber is this app's "closing soon" color everywhere, so the badge reads
   the same as the countdowns on the tender list. #fbbf24 behind #0b1220
   text is 11:1. */
.nav-badge {
    flex-shrink: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #fbbf24;
    color: #0b1220;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.nav-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 0 0 2px #001F3F;
}

.nav-dot--icon {
    position: absolute;
    top: -2px;
    right: -3px;
}

.nav-dot--inline {
    flex-shrink: 0;
}

/* ===== Items outside the current plan =====
   Readable and clickable — tapping one explains why it's unavailable (the
   upgrade prompt). The backend 403 remains the real enforcement. */
.sidebar-pill.plan-locked {
    color: rgba(255, 255, 255, 0.6);
}

.module-lock-icon {
    margin-left: auto;
    flex-shrink: 0;
    opacity: 0.75;
}

/* ===== Hover peek: full width over the page, with a shadow to show it
   floats above the content rather than pushing it. ===== */
.glass-sidebar.peek {
    width: 220px;
    box-shadow: 8px 0 32px rgba(0, 31, 63, 0.35);
}

/* ===== Rail tooltip (teleported to <body>) ===== */
.rail-tooltip {
    position: fixed;
    z-index: 2100;
    transform: translateY(-50%);
    padding: 5px 9px;
    border-radius: 6px;
    background: #0b1220;
    color: #fff;
    font-family: var(--font-sans);
    font-size: 0.78rem;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 6px 18px rgba(2, 8, 23, 0.28);
    pointer-events: none;
}
</style>
